import { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { Appearance, DraftConfiguration, PrintArea, View } from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { assertDefined } from '@/utils/assertDefined';
import { getPossiblePrintTypes } from '@/model/configuration/getPossiblePrintTypes';
import { getPrintAreaForView } from '@/model/productType/getPrintAreaForView';
import { useReduxDraftDispatch } from '@/redux/draft/draftDispatch';
import { getCurrentAppearance } from '@/redux/draft/getters/getCurrentAppearance';
import { getProductType } from '@/redux/draft/getters/getProductType';
import { selectConfigurations } from '@/redux/draft/getters/selectConfigurations';
import { StoreState } from '@/redux/types';
import { switchPrintAreaAction } from '@/components/draft/draftState/actions/switchPrintAreaAction';
import { Viewer } from '../draft/Viewer';
import { CheckMark } from './icons/CheckMark';
import './PrintAreaSelector.scss';

interface TileProps {
  appearance: Appearance;
  view: View;
  printArea: PrintArea;
  selected: boolean;
  disabled: boolean;
  onClick: (printArea: PrintArea) => void;
}

const PrintAreaTile: FunctionComponent<TileProps> = ({
  appearance,
  view,
  printArea,
  selected,
  disabled,
  onClick,
}) => (
  <button
    type="button"
    className={cn('print-area-tile', { 'print-area-tile--active': selected })}
    onClick={() => onClick(printArea)}
    disabled={disabled}
    role="option"
    aria-selected={selected}
    title={view.name}
  >
    <Viewer
      view={view}
      appearance={appearance}
      printArea={printArea}
      className="print-area-tile__viewer"
      configurations={[]}
      hideMask={true}
    />
    {selected && (
      <div className="print-area-tile__check">
        <CheckMark width="1.5em" height="1.5em" />
      </div>
    )}
  </button>
);

interface PrintAreaSelectorProps {
  configuration: DraftConfiguration;
  onClose: () => void;
}

export const PrintAreaSelector: FunctionComponent<PrintAreaSelectorProps> = ({
  configuration,
  onClose,
}) => {
  const dispatch = useReduxDraftDispatch();
  const productType = useSelector(getProductType);
  const appearance = useSelector(getCurrentAppearance);
  const viewsToRender = useSelector((state: StoreState) => {
    const configurations = selectConfigurations(state);
    const views = productType.views.filter((v) => v.viewMaps.length > 0);
    return views.map((view) => {
      // if a view has a viewMap it must have a print area
      const printArea = assertDefined(getPrintAreaForView(productType, view, configurations));
      const printable = getPossiblePrintTypes(configuration, printArea, appearance).length > 0;
      return { view, printArea, printable };
    });
  });

  function handlePrintAreaSelect(printArea: PrintArea) {
    trackFeature('move-configuration-to-other-view');
    dispatch(switchPrintAreaAction(configuration, printArea));
    onClose();
  }

  return (
    <div className="print-area-selector" role="listbox">
      {viewsToRender.map(({ view, printArea, printable }) => (
        <PrintAreaTile
          appearance={appearance}
          view={view}
          printArea={printArea}
          selected={printArea === configuration.printArea}
          disabled={!printable}
          onClick={handlePrintAreaSelect}
          key={view.id}
        />
      ))}
    </div>
  );
};
